import { z } from 'zod';

const version = z.number().int().min(0).max(4294967294);
export const PlanDraftSchema = z.object({
  productId: z.enum(['MONTHLY', 'YEARLY', 'TWO_YEAR', 'TRAFFIC_RESET']),
  cnyAmount: z.string().regex(/^(?:0|[1-9]\d{0,9})\.\d{2}$/, '請輸入兩位小數的人民幣金額').refine(v => v !== '0.00', '金額必須大於零'),
  benefitProfileVersion: z.number().int().positive().max(4294967295).nullable(),
  expectedLatestVersion: version,
  localizations: z.array(z.object({ locale: z.string().max(35).regex(/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/, '請輸入有效語系，例如 zh-CN 或 en'), displayName: z.string().trim().max(120), highlights: z.string().refine(value => { const rows = value.split('\n').map(v => v.trim()).filter(Boolean); return rows.length <= 10 && rows.every(v => v.length <= 200); }, '最多 10 項，每項最多 200 字'), badge: z.string().trim().max(64), sortOrder: z.number().int().min(0).max(100000) })).max(10).refine(values => new Set(values.map(v => v.locale)).size === values.length, '語系不可重複'),
});
export type PlanDraftDTO = z.infer<typeof PlanDraftSchema>;

export const BenefitDraftSchema = z.object({
  expectedLatestVersion: version,
  tierId: z.number().int().positive().max(2147483647).nullable(),
  usagePolicy: z.literal('ACCOUNT_PERIOD_QUOTA_V1').optional(),
}).strict();
export type BenefitDraftDTO = z.infer<typeof BenefitDraftSchema>;

export const MemberLookupSchema = z.object({ memberAccountId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER) });
export type MemberLookupDTO = z.infer<typeof MemberLookupSchema>;
export const ManualOperationSchema = z.object({
  memberAccountId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  expectedSubscriptionVersion: version,
  reason: z.string().trim().min(1, '請填寫原因').max(500),
  durationUnit: z.enum(['MONTH', 'DAY', 'HOUR']),
  durationCount: z.number().int().positive().max(4294967295),
  grantId: z.number().int().positive().nullable(),
  benefitProfileVersion: z.number().int().positive().nullable(),
}).refine(value => value.durationCount * (value.durationUnit === 'MONTH' ? 2678400 : value.durationUnit === 'DAY' ? 86400 : 3600) <= 4294967295, { path: ['durationCount'], message: '換算後的期間超過可發放範圍' });
export type ManualOperationDTO = z.infer<typeof ManualOperationSchema>;
export const EvidenceExportSchema = z.object({ reason: z.string().trim().min(1, '請填寫匯出理由').max(500).refine(v => !/[\p{Cc}]/u.test(v), '理由不可含控制字元') });
export type EvidenceExportDTO = z.infer<typeof EvidenceExportSchema>;
export const PlanScheduleSchema = z.object({ scheduledAt: z.string().refine(v => !v || (Number.isFinite(Date.parse(v)) && Date.parse(v) > Date.now()), '排程必須是未來的有效時間') });
export type PlanScheduleDTO = z.infer<typeof PlanScheduleSchema>;

export const PaymentConfigUpdateSchema = z.object({
  purchaseEnabled: z.boolean({ required_error: '請明確選擇開啟或關閉購買' }),
  reason: z.string().trim().min(1, '請填寫變更原因').max(500).refine(v => !/[\p{Cc}]/u.test(v), '原因不可含控制字元'),
});
export type PaymentConfigUpdateDTO = z.infer<typeof PaymentConfigUpdateSchema>;

export const PaymentChannelUpdateSchema = z.object({
  enabledMethods: z.array(z.enum(['ALIPAYH5', 'WECHAT'])).max(2).refine(v => new Set(v).size === v.length, '渠道不可重複'),
  reason: z.string().trim().min(1, '請填寫變更原因').max(500).refine(v => !/[\p{Cc}]/u.test(v), '原因不可含控制字元'),
}).strict();
export type PaymentChannelUpdateDTO = z.infer<typeof PaymentChannelUpdateSchema>;

export const PlanDraftDeleteSchema = z.object({ reason: z.string().trim().min(1, '請填寫刪除原因').max(500) }).strict();
export type PlanDraftDeleteDTO = z.infer<typeof PlanDraftDeleteSchema>;

export const ManualMemberSearchSchema = z.object({ searchBy: z.enum(['id', 'username']), query: z.string().trim().min(1, '請輸入會員 ID 或會員帳號').max(100).refine(v => !/[\p{Cc}]/u.test(v), '搜尋內容不可含控制字元') });
export type ManualMemberSearchDTO = z.infer<typeof ManualMemberSearchSchema>;

export const QuotaCompensationSchema = z.object({
  reason: z.string().trim().min(1, '請填寫補發原因').max(500).refine(v => !/[\p{Cc}]/u.test(v), '原因不可含控制字元'),
}).strict();
export type QuotaCompensationDTO = z.infer<typeof QuotaCompensationSchema>;

export const BenefitRetirementSchema = z.object({ reason: z.string().trim().min(1, '請填寫退役原因').max(500).refine(v => !/[\p{Cc}]/u.test(v), '原因不可含控制字元') }).strict();
export type BenefitRetirementDTO = z.infer<typeof BenefitRetirementSchema>;

const quotaGbPattern = /^(?:0|[1-9]\d{0,4})(?:\.\d{1,3})?$/;
export const QuotaGbSchema = z.string().regex(quotaGbPattern, '請輸入最多 3 位小數的 GB').refine(value => {
  if (!quotaGbPattern.test(value)) return false;
  const [whole, fraction = ''] = value.split('.');
  const megabytes = BigInt(whole) * 1000n + BigInt(fraction.padEnd(3, '0'));
  return megabytes >= 1n && megabytes <= 10000000n;
}, '額度須為 0.001–10000 GB（最小 1 MB）');
export const BrandQuotaFormSchema = z.object({
  quotaGb: QuotaGbSchema,
  reason: z.string().trim().min(1, '請填寫變更原因').max(500).refine(v => !/[\p{Cc}]/u.test(v), '原因不可含控制字元'),
}).strict();
export type BrandQuotaFormDTO = z.infer<typeof BrandQuotaFormSchema>;


export const PaymentExpirySchema = z.object({
  reason: z.string().trim().min(1, '請填寫過期原因').max(500, '原因最多 500 字').refine(value => !/[\p{Cc}]/u.test(value), '原因不可含控制字元'),
}).strict();
export type PaymentExpiryDTO = z.infer<typeof PaymentExpirySchema>;
